import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userApi, useUser } from '@features/user';
import { useRecordingSession } from '@hooks/useRecordingSession';
import { Box, Modal, Slide, Step, StepLabel, Stepper } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';

import { LogoutButton } from '../components/Buttons/LogoutButton';
import { CardGrid } from '../components/CardGrid';
import { GradientTitle } from '../components/CardGrid/GradientTitle';
import LanguageAndNotePreferencesForm from '../components/LanguageAndNotePreferencesForm';
import { UserInfoForm } from '../components/UserInfoForm';
import { useThemeContext } from '../context/theme/context';
import { useUIState } from '../context/uiState';
import { NotesLoader } from '../loaders/DashboardLoader';
import { trackEvent } from '../utils/analytics_utils';

const OnboardingViewRevamp: React.FC = () => {
  const { completeSignup, userState } = useUser();
  const { state } = useUIState();
  const { platform } = state;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { onboarding: onboardingMetrics } = useEventTracking();
  const { recordingPath } = useRecordingSession();
  const [activeStep, setActiveStep] = useState<number>(userState?.userInfo ? 1 : 0);
  const [contentKey, setContentKey] = useState<number>(0);

  const { brandLanguage } = useThemeContext();

  // Users get 2 steps in the onboarding flow
  const maxSteps = 2;
  const [firstNoteId, setFirstNoteId] = useState<string | null>(null);

  // Fetch notes when user reaches step 1 (the second step)
  useEffect(() => {
    const loadFirstNote = async () => {
      if (userState?.user_id && activeStep === 1) {
        try {
          const { notes } = await NotesLoader('all', 1); // Get just the first note
          if (notes.length > 0) {
            setFirstNoteId(notes[0].note_id);
          }
        } catch (error) {
          console.error('Failed to load notes:', error);
        }
      }
    };

    void loadFirstNote();
  }, [userState?.user_id, activeStep]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeStep]);

  // Track onboarding started when component mounts
  useEffect(() => {
    onboardingMetrics.started({
      initial_step: activeStep === 0 ? 'user_info' : 'preferences',
      has_existing_profile: !!userState?.userInfo,
      platform: platform,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only runs once on mount

  const handleNext = (): void => {
    if (activeStep < maxSteps - 1) {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
      setContentKey(prevKey => prevKey + 1); // Trigger re-render with new key
    } else {
      // Complete onboarding after step 2
      onCompleteOnboarding();
    }
  };

  const onCompleteOnboarding = (location: string = 'dashboard'): void => {
    // Track onboarding completion
    onboardingMetrics.completed({
      completion_path: location,
      steps_completed: activeStep + 1,
    });
    
    completeSignup();
    navigate(location);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Conditional rendering based on activeStep
  const getStepContent = (step: number): React.ReactElement => {
    switch (step) {
      case 0:
        return (
          <UserInfoForm
            onSuccessfulFormSubmission={() => {
              // Invalidate related caches after user info update during onboarding
              dispatch(userApi.util.invalidateTags(['Templates', 'TroubleWords', 'Tutorials']));

              trackEvent({
                event: 'signupConfirm',
                platform: platform,
              });
              trackEvent({
                event: 'user_info_form_completed',
                platform: platform,
              });
              handleNext();
            }}
          />
        );
      case 1:
        return (
          <LanguageAndNotePreferencesForm
            onSuccessfulFormSubmission={() => {
              // Track preferences completion
              onboardingMetrics.preferencesSet({
                is_final_step: true,
              });
              
              // Keep legacy tracking for backward compatibility
              trackEvent({
                event: 'onboarding_language_preferences_screen_complete',
                platform: platform,
              });
              handleNext();
            }}
          />
        );

      case 2: {
        // This step is no longer used (was for control variant)
        // Redirect to dashboard
        return <div>Redirecting to dashboard...</div>;
        
        const cards = [
          {
            id: '1',
            title: 'Record a demo visit',
            description:
              "We'll give you a sample transcript. Read it aloud and we'll turn your recording into a real note -- so you can see how it works!",
            image: (
              <Box
                component="img"
                src="/onboarding-images/record-demo.svg"
                alt="Record a demo visit"
                sx={{ width: '80%', height: '80%', objectFit: 'contain' }}
              />
            ),
            action: {
              label: 'Record a demo visit',
              onClick: () => {
                onboardingMetrics.pathSelected('demo');
                onCompleteOnboarding('/first-encounter');
              },
            },
          },
          {
            id: '2',
            title: 'Skip the recording',
            description:
              "No time to record? We'll show you a note generated from a sample session so you can see how it looks and what's included",
            image: (
              <Box
                component="img"
                src="/onboarding-images/skip-recording.svg"
                alt="Skip the recording"
                sx={{ width: '80%', height: '80%', objectFit: 'contain' }}
              />
            ),
            action: {
              label: 'View a sample note',
              onClick: () => {
                onboardingMetrics.pathSelected('skip');
                if (firstNoteId) {
                  onCompleteOnboarding(`/note/${firstNoteId}`);
                } else {
                  // Fallback if no notes exist
                  onCompleteOnboarding();
                }
              },
            },
          },
          {
            id: '3',
            title: 'Record a live visit',
            description: "Record an actual visit and we'll generate your note automatically",
            image: (
              <Box
                component="img"
                src="/onboarding-images/record-live.svg"
                alt="Record a live visit"
                sx={{ width: '80%', height: '80%', objectFit: 'contain' }}
              />
            ),
            action: {
              label: 'Record a live visit',
              onClick: () => {
                onboardingMetrics.pathSelected('live');
                onCompleteOnboarding(recordingPath);
              },
            },
          },
        ];

        return (
          <CardGrid
            title={<GradientTitle>Let&apos;s start using {brandLanguage.brandName}!</GradientTitle>}
            cards={cards}
            fullHeight={false}
          />
        );
      }

      default:
        return <div>Unknown step</div>;
    }
  };

  const HorizontalTimeline = () => {
    const steps = ['Get Started', 'Basic Settings'];
    return (
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          '& .MuiStepConnector-line': {
            borderTopWidth: 3,
          },
          '& .MuiStepLabel-label': {
            fontSize: '0.95rem',
            fontWeight: 500,
            display: { xs: 'none', sm: 'block' },
          },
        }}
      >
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    );
  };

  return (
    <>
      <Modal
        open={true}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
        }}
      >
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fafafa',
            position: 'relative',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pt: { xs: 4, sm: 6, md: 8 },
              pb: 4,
              px: 2,
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: activeStep === 2 ? '100%' : '800px',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '800px',
                  mx: 'auto',
                  flexShrink: 0,
                  mb: 4,
                }}
              >
                <HorizontalTimeline />
              </Box>
              <Slide
                id="slide"
                direction="left"
                in={true}
                mountOnEnter
                unmountOnExit
                key={contentKey}
                style={{ transformOrigin: 'right' }}
              >
                <Box
                  sx={{
                    width: '100%',
                    flex: 1,
                    ...(activeStep !== 2 && {
                      backgroundColor: 'white',
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      px: { xs: 2, sm: 3, md: 4 },
                      py: { xs: 3, md: 4 },
                    }),
                  }}
                >
                  {getStepContent(activeStep)}
                </Box>
              </Slide>
            </Box>
          </Box>

          {/* Bottom buttons container */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 4,
              pb: 4,
              mt: 'auto',
            }}
          >
            <LogoutButton size="small" />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default OnboardingViewRevamp;
